import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import { ICONS, type IconName } from '@/components/ui/icons';

import { useOnASheet } from './on-a-sheet';

export interface EmptyStateProps {
  /** Falls back to the shared `empty.default.*` copy, so an empty list is never a blank box. */
  title?: string;
  description?: string;
  icon?: IconName;
  action?: ReactNode;
  compact?: boolean;
  className?: string;
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  compact = false,
  className,
}: EmptyStateProps): ReactNode {
  const { t } = useTranslation();
  const Icon = icon ? ICONS[icon] : null;
  const heading = title ?? t('empty.default.title');
  const body = description ?? (title === undefined ? t('empty.default.description') : undefined);
  /*
   * INGEN STIPLET BOKS (Glass 2, §16.5 punkt 8 og 9). På et ark (kort, dialog, tabell) står den
   * tomme tilstanden rett på arket; på siden får den sitt eget, så teksten aldri står på bakgrunnen.
   */
  const sheet = useOnASheet();

  return (
    <div
      ref={sheet.ref}
      className={cn(
        'flex flex-col items-center justify-center gap-1.5 text-center',
        !sheet.onSheet && 'glass-content',
        compact ? 'px-3 py-5' : 'px-6 py-10',
        className,
      )}
    >
      {Icon ? <Icon aria-hidden className="size-4 text-subtle" /> : null}
      <p className="text-xs font-medium text-default">{heading}</p>
      {body ? <p className="max-w-sm text-sm text-subtle">{body}</p> : null}
      {action ? <div className="mt-1.5">{action}</div> : null}
    </div>
  );
}
